/**
 * Event helpers with no React in them — dates, and the checks worth
 * making before a save leaves the browser.
 *
 * **Days are not instants.** `starts_on` and `ends_on` are `YYYY-MM-DD`, and
 * `new Date("2026-03-03")` parses that as UTC midnight: every browser west of
 * Greenwich then renders a conference starting on the 3rd as starting on the
 * 2nd. Every date here is built from its parts instead.
 *
 * **`validateEvent` is not a second copy of the server's rules.** The server
 * is still what decides. Two of these checks exist because the constraint that
 * catches them lives in the database — `ends_on >= starts_on`, and one slug per
 * site — and a database constraint arrives as a 500 with nothing to show the
 * person who typed the value. The rest are here to say "for example
 * kuwait-phc-2026" where the framework would say "String should match pattern".
 */

import type { EventInput, EventRecord, FieldProblem } from "@/lib/types";

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A `YYYY-MM-DD` day as a local date, or null if it is not one. */
export function parseDay(day: string): Date | null {
  const match = DAY.exec(day);
  if (!match) return null;
  const [, year, month, date] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(date));
  // Rejects 2026-02-30, which the constructor would roll forward to March.
  return parsed.getMonth() === Number(month) - 1 ? parsed : null;
}

/** A local date as `YYYY-MM-DD`. `toISOString` would answer in UTC. */
export function formatDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Every day from `startsOn` to `endsOn` inclusive, as `YYYY-MM-DD`. Empty
 * when either is not a day or the range runs backwards. */
export function eventDays(startsOn: string, endsOn: string): string[] {
  const first = parseDay(startsOn);
  const last = parseDay(endsOn);
  const days: string[] = [];
  if (!first || !last) return days;
  for (const day = first; day <= last; day.setDate(day.getDate() + 1)) {
    days.push(formatDay(day));
  }
  return days;
}

/** Today in the event's timezone, as `YYYY-MM-DD`. A desk in another zone, or
 * a laptop left on UTC, must not mark somebody present on the wrong day. An
 * unknown zone falls back to this browser's, as the server falls back to UTC. */
export function todayIn(timezone: string, now: Date = new Date()): string {
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(now);
  } catch {
    return formatDay(now);
  }
}

/** What the clock on the wall reads in `timezone` at `at`, as milliseconds the
 * way `Date.UTC` counts them. */
function clock(at: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(at);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((one) => one.type === type)?.value);
  return Date.UTC(
    part("year"),
    part("month") - 1,
    part("day"),
    part("hour"),
    part("minute"),
    part("second"),
  );
}

/** `2026-11-03T09:00`, what a `datetime-local` input holds, for an instant as
 * the event's own clock reads it. The input has no zone: the server's UTC text
 * sliced into it showed a 09:00 session in Kuwait as 06:00. */
export function wallClock(iso: string, timezone: string): string {
  return new Date(clock(new Date(iso), timezone)).toISOString().slice(0, 16);
}

/** The instant a `datetime-local` value names on the event's own clock, in UTC:
 * `wallClock` the other way. Sent without a zone, the server takes it as UTC,
 * and a 09:00 typed in Kuwait was kept as noon. Anything that is not a time
 * goes as typed, for the server to refuse. */
export function instantOf(local: string, timezone: string): string {
  const wall = Date.parse(`${local}Z`);
  if (Number.isNaN(wall)) return local;
  // The zone's offset is read at the first guess and again at the answer: the
  // guess can fall on the other side of a daylight-saving change.
  const offset = (at: number) => clock(new Date(at), timezone) - Math.floor(at / 1000) * 1000;
  const guess = wall - offset(wall);
  return new Date(wall - offset(guess)).toISOString();
}

const RANGE = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
  year: "numeric",
});

/** "3–5 March 2026", collapsed by the platform's own range formatter — and a
 * single date when the event runs for one day. */
export function formatDateRange(startsOn: string, endsOn: string): string {
  const start = parseDay(startsOn);
  const end = parseDay(endsOn);
  if (!start || !end) return `${startsOn} – ${endsOn}`;
  return RANGE.formatRange(start, end);
}

/** The one a conference opens on: the newest by start date, or null if it has
 *  none yet.
 *
 *  Pressing a conference goes straight to this event's workspace rather than to
 *  a list. For the sites here that list has exactly one row, already chosen by
 *  the press before it — a step that only asks you to confirm what you just
 *  said. Sorted rather than trusting the server's order. */
export function newestEvent(events: readonly EventRecord[]): EventRecord | null {
  let newest: EventRecord | null = null;
  for (const event of events) {
    if (!newest || event.starts_on.localeCompare(newest.starts_on) > 0) newest = event;
  }
  return newest;
}

export type Phase = "upcoming" | "now" | "over";

/** Where a conference stands today, by its events: in progress while one runs
 * (today on that event's own clock), else upcoming by its soonest still to
 * come, else over by the last to end. Null with no events yet, so a
 * conference is never filed under a date it does not have. Days compare as
 * strings: `YYYY-MM-DD` sorts as it reads. */
export function whenOf(
  events: readonly EventRecord[],
  now: Date = new Date(),
): { phase: Phase; event: EventRecord } | null {
  let soonest: EventRecord | null = null;
  let latest: EventRecord | null = null;
  for (const event of events) {
    const today = todayIn(event.timezone, now);
    if (event.starts_on <= today && today <= event.ends_on) return { phase: "now", event };
    if (event.starts_on > today) {
      if (!soonest || event.starts_on < soonest.starts_on) soonest = event;
    } else if (!latest || event.ends_on > latest.ends_on) {
      latest = event;
    }
  }
  if (soonest) return { phase: "upcoming", event: soonest };
  return latest ? { phase: "over", event: latest } : null;
}

/** A name as a slug the server will accept, or "" if nothing survives — a
 * name in Arabic leaves nothing, and then the client types one. */
export function slugify(name: string): string {
  return (
    name
      .normalize("NFKD")
      // The accents NFKD just separated, so "Café" slugs as "cafe" not "caf-e".
      .replace(/\p{Mn}/gu, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  );
}

/** The editable body of an existing event.
 *
 * Written out field by field rather than by dropping `id`, so a field added to
 * the API fails here at compile time instead of being quietly left out of a
 * PATCH that would then clear it. */
export function toInput(event: EventRecord): EventInput {
  return {
    name: event.name,
    slug: event.slug,
    status: event.status,
    kind: event.kind,
    starts_on: event.starts_on,
    ends_on: event.ends_on,
    timezone: event.timezone,
    currency: event.currency,
    venue_id: event.venue_id,
    website_url: event.website_url,
    theme_colour: event.theme_colour,
  };
}

/** What is wrong with this draft, in the same shape a server 422 arrives in,
 * so a form can render both from one list. `taken` is the slugs belonging to
 * every *other* event on the site. */
export function validateEvent(draft: EventInput, taken: readonly string[]): FieldProblem[] {
  const problems: FieldProblem[] = [];
  const problem = (field: string, message: string) => problems.push({ path: [field], message });

  if (!draft.name.trim()) problem("name", "An event needs a name.");

  if (!SLUG.test(draft.slug)) {
    problem(
      "slug",
      "Lowercase letters, numbers and single hyphens only — for example kuwait-phc-2026.",
    );
  } else if (taken.includes(draft.slug)) {
    problem("slug", "Another event on this site already uses this slug.");
  }

  const starts = parseDay(draft.starts_on);
  const ends = parseDay(draft.ends_on);
  if (!starts) problem("starts_on", "Pick the day the event starts.");
  if (!ends) problem("ends_on", "Pick the day the event ends.");
  else if (starts && draft.ends_on < draft.starts_on) {
    problem("ends_on", "An event cannot end before it starts.");
  }

  if (!draft.timezone.trim()) {
    problem("timezone", "A check-in time without a timezone is ambiguous.");
  }
  if (draft.currency.trim().length !== 3) {
    problem("currency", "A three-letter code, such as KWD.");
  }

  return problems;
}
